import type {
  LegalPage,
  LanguageKey,
  TreatmentTemplateDetails,
  ExerciseDetails,
  Sound,
  MistakeText,
  MistakeVideo,
} from "@akina/domain";
import {
  type NullableCmsCopy,
  type CmsGetType,
  type CmsSetType,
  type CmsStoryContent,
  type CmsMistakeBlock,
  CMS_PATHS,
  slugifyCmsKey,
  type ReleaseNotes,
} from "@akina/infra";
import { resolveMediaStreamingHost } from "./getMediaStreamingUrl";

export const setCmsCopy = (set: CmsSetType) => (newCopy: NullableCmsCopy) => {
  set(state => ({ ...state, cmsCopy: newCopy }));
};

const mistakeBlocksOf = (story: CmsStoryContent): CmsMistakeBlock[] => [
  ...(story.clinically_meaningful ?? []),
  ...(story.tips ?? []),
  ...(story.non_clinically_meaningful ?? []),
];

export const getExerciseMistakeVideos =
  (get: CmsGetType) =>
  (exerciseKey: string): MistakeVideo[] => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories) return [];

    const slug = CMS_PATHS.EXERCISE_MISTAKES(slugifyCmsKey(exerciseKey));
    const story = cmsCopy.otherStories[slug];
    if (!story) return [];

    return mistakeBlocksOf(story).flatMap(block => {
      const mistakesVEPool = block.vc_pool?.find(v => v.key === "mistake")?.ve_pool ?? [];
      return mistakesVEPool.map(v => ({
        key: v.key,
        url: v.media_url.url,
        duration: v.duration,
      }));
    });
  };

export const getExerciseMistakesText = (get: CmsGetType) => (): MistakeText[] => {
  const cmsCopy = get().cmsCopy;
  if (!cmsCopy?.otherStories) return [];

  // A mistake's full block is embedded in every exercise that uses it, so the
  // same mistake recurs across stories — dedupe by its kebab `key`.
  const mistakesByKey = new Map<string, MistakeText>();

  for (const [slug, story] of Object.entries(cmsCopy.otherStories)) {
    if (!slug.startsWith(CMS_PATHS.EXERCISES_MISTAKES)) continue;

    for (const block of mistakeBlocksOf(story)) {
      if (block.key && block.DisplayedName && block.Description && !mistakesByKey.has(block.key)) {
        mistakesByKey.set(block.key, {
          key: block.key,
          name: block.DisplayedName,
          description: block.Description,
        });
      }
    }
  }

  // Generic mistakes are not linked to exercises — they live in their own `generic-mistakes/{mistake}` stories.
  for (const [slug, story] of Object.entries(cmsCopy.otherStories)) {
    if (!slug.startsWith(CMS_PATHS.GENERIC_MISTAKES)) continue;

    if (story.slug && story.DisplayedName && story.Description && !mistakesByKey.has(story.slug)) {
      mistakesByKey.set(story.slug, {
        key: story.slug,
        name: story.DisplayedName,
        description: story.Description,
      });
    }
  }

  return [...mistakesByKey.values()];
};

export const getSoundByKey =
  (get: CmsGetType) =>
  (type: Sound["type"], key: string): Sound | undefined => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories) return undefined;

    const slug = `${CMS_PATHS.SOUND(type)}/${key}`;
    const story = Object.entries(cmsCopy.otherStories).find(([storyKey]) => storyKey === slug)?.[1];

    return story
      ? ({
          key: story.slug,
          name: story.Description,
          url: story.URLLink?.url,
          type,
          isDisabled: story.IsDisabled,
        } as Sound)
      : undefined;
  };

export const getImageByKey =
  (get: CmsGetType) =>
  (key: string): string | undefined => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories) return undefined;

    const slug = CMS_PATHS.IMAGE(key);
    const story = cmsCopy.otherStories[slug];

    return (story?.MediaURL?.filename as string) ?? undefined;
  };

export const getLegalPage =
  (get: CmsGetType) =>
  (key: LegalPage["key"], app: "care" | "portal", language?: LanguageKey | null): LegalPage | undefined => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories) return undefined;

    const baseSlug = CMS_PATHS.LEGAL_PAGE[app];
    const languageSuffix = key === "instructions-for-use" && language ? `-${language.toLowerCase()}` : "";
    const slug = `${baseSlug}${key}${languageSuffix}`;
    const story = cmsCopy.otherStories[slug];
    if (!story) return undefined;


    return {
      key,
      title: story.title,
      subtitle: story.subtitle,
      version: story.version,
      date: story.date ? new Date(story.date) : undefined,
      content: resolveMediaStreamingHost(story.content),
      pdfUrl: story.PDF?.filename ? resolveMediaStreamingHost(story.PDF.filename) : undefined,
      preContent: story.preContent ? resolveMediaStreamingHost(story.preContent) : undefined,
    } as LegalPage;
  };

export const getIFULanguages = (get: CmsGetType) => (): { key: LanguageKey; name: string }[] => {
  const cmsCopy = get().cmsCopy;
  if (!cmsCopy) return [];
  return Object.entries(cmsCopy.otherStories)
    .filter(([key, content]) => key.startsWith(CMS_PATHS.IFU_LANGUAGES) && content.Text)
    .map(([, content]) => ({
      key: content.slug as LanguageKey,
      name: content.Text ?? "",
    }));
};

export const getTreatmentTemplateDetails =
  (get: CmsGetType) =>
  (key: string | undefined): TreatmentTemplateDetails | undefined => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories || !key) return undefined;

    const slug = CMS_PATHS.TREATMENT_TEMPLATE(slugifyCmsKey(key));
    const story = cmsCopy.otherStories[slug];

    if (!story) return undefined;

    return {
      bodyPart: story.BodyPart,
      key: story.key,
      name: story.name,
      description: story.Description,
      phases: story.phases?.map(p => ({
        key: p.key,
        goal: p.goal,
        name: p.name,
      })),
    } as TreatmentTemplateDetails;
  };

export const getTreatmentTemplatesDetails = (get: CmsGetType) => (): TreatmentTemplateDetails[] => {
  const cmsCopy = get().cmsCopy;
  if (!cmsCopy?.otherStories) return [];

  return Object.entries(cmsCopy.otherStories)
    .filter(([key, content]) => key.startsWith(CMS_PATHS.TREATMENT_TEMPLATES) && content.name && content.Description)
    .map(([, content]) => ({
      bodyPart: content.BodyPart,
      key: content.key,
      name: content.name,
      description: content.Description,
      phases: content.phases?.map(p => ({
        key: p.key,
        goal: p.goal,
        name: p.name,
      })),
    })) as TreatmentTemplateDetails[];
};

export const getTreatmentTemplatesListDetails = (get: CmsGetType) => () => {
  const cmsCopy = get().cmsCopy;
  if (!cmsCopy?.otherStories) return [];

  return Object.entries(cmsCopy.otherStories)
    .filter(([key, content]) => key.startsWith(CMS_PATHS.TREATMENT_TEMPLATES) && content.name)
    .map(([, content]) => ({
      key: content.key,
      name: content.name,
      bodyPart: content.BodyPart,
      description: content.Description,
    }));
};

export const getExerciseDetails =
  (get: CmsGetType) =>
  (exerciseKey: string): ExerciseDetails | undefined => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories) return undefined;

    const slug = CMS_PATHS.EXERCISE(slugifyCmsKey(exerciseKey));
    const story = cmsCopy.otherStories[slug];
    if (!story) return undefined;

    return {
      key: exerciseKey,
      description: story.Description,
      name: story.DisplayedName,
      imageUrl: story.Thumbnail?.filename,
      previewGif: story.PreviewGif?.filename,
      instructions: story.InstructionTextBullets?.map(s => s.Text) || [],
      firstInstructionVideoUrl: story?.vc_pool?.find(v => v.key === "instruction")?.ve_pool?.[0]?.media_url?.url,
    } as ExerciseDetails;
  };

export const getAllExerciseNames = (get: CmsGetType) => (): { key: string; name: string }[] => {
  const cmsCopy = get().cmsCopy;
  if (!cmsCopy?.otherStories) return [];

  const slugToExerciseKey = (slug: string | undefined): string => {
    if (!slug) return "";
    return slug.split("-").join("_").toLowerCase().replace("left", "right");
  };

  return Object.entries(cmsCopy.otherStories)
    .filter(([key, content]) => key.startsWith(CMS_PATHS.EXERCISE_NAMES) && content.DisplayedName)
    .map(([, content]) => ({ key: slugToExerciseKey(content.slug), name: content.DisplayedName as string }));
};

export const getReleaseNotes =
  (get: CmsGetType) =>
  (app: "care" | "portal"): ReleaseNotes | undefined => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories) return undefined;

    const slug = CMS_PATHS.RELEASE_NOTES[app];
    const story = cmsCopy.otherStories[slug];
    if (!story) return undefined;

    return {
      pageTitle: story.PageTitle ?? "",
      pageSubtitle: story.PageSubtitle ?? "",
      tableOfContentsTitle: story.TableOfContentsTitle ?? "",
      releases:
        story.Releases?.map(release => ({
          releaseKey: release.ReleaseKey,
          releaseTitle: release.ReleaseTitle ?? "",
          releaseDate: release.ReleaseDate,
          content: release.Content,
        })) || [],
    };
  };
export const getSounds =
  (get: CmsGetType) =>
  (type: Sound["type"]): Sound[] => {
    const cmsCopy = get().cmsCopy;
    if (!cmsCopy?.otherStories) return [];

    const slug = CMS_PATHS.SOUND(slugifyCmsKey(type) as Sound["type"]);
    return Object.entries(cmsCopy.otherStories)
      .filter(([key, content]) => key.startsWith(slug) && content.Description)
      .map(([, content]) => ({
        key: content.slug,
        name: content.Description,
        url: content.URLLink?.url,
        type: type,
        musicType: content.MusicType,
        isDisabled: content.IsDisabled,
      })) as Sound[];
  };
